import { Graphics } from '@inlet/react-pixi';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { useDrawTagCircle } from 'components/powerFlowShared/hooks/useDrawTagCircle';
import { flowNodeColor } from 'fable/flowEditor';
import { AutomationExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { TagModal } from '../../modals/tag';
import { TagOperation } from '../types';

export type Tag = {
  id: string;
  name: string;
  color: string;
};

export type Payload = {
  operation: TagOperation;
  tags: Tag[];
  nextNodeId: string;
};

type TagsNodeAttributes = GenericNodeAttributes<Payload, AutomationExtraData>;
type TagsNodeProps = GenericNodeProps<Payload, AutomationExtraData>;

const translatePayload: TagsNodeAttributes['translatePayload'] = (node) => {
  const { nextNodeId } = node.payload;

  return [
    {
      id: uuid(),
      label: 'arrow',
      connection: nextNodeId || uuid(),
    },
  ];
};

const updateConnection: TagsNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = { ...node.payload, nextNodeId: connectionId };

  return { ...node, payload };
};

export const TagsNodeAttributes: TagsNodeAttributes = {
  iconName: 'tag',
  label: 'Tags',
  color: hexStringToNumber(flowNodeColor.blue),
  translatePayload,
  updateConnection,
  NodeContent: TagsNode,
  ActionModal: TagModal,
};

export function TagsNode({ node }: TagsNodeProps): JSX.Element {
  const { payload } = node;
  const { operation, tags } = payload;
  const [firstTag] = tags;

  const drawTagCircle = useDrawTagCircle(firstTag, { x: 5, y: 35 });
  const labelEndCharacter = tags.length !== 1 ? 's' : '';
  const label =
    operation === TagOperation.TAG
      ? `Add tag${labelEndCharacter}:`
      : `Remove tag${labelEndCharacter}:`;

  return (
    <>
      <TextContent>
        {useMemo(
          () => (
            <TextEllipsis>
              <Text weight="normal" text={label} />
            </TextEllipsis>
          ),
          [label],
        )}
      </TextContent>
      <Graphics draw={drawTagCircle} />
      <TextContent width={200} offset={{ x: 15, y: 22 }}>
        {useMemo(
          () => (
            <TextEllipsis>
              <Text weight="semibold" text={firstTag?.name || 'unknown tag'} />
            </TextEllipsis>
          ),
          [firstTag],
        )}
      </TextContent>
      <TextContent width={230} offset={{ x: 200, y: 22 }}>
        {useMemo(() => {
          if (tags.length < 2) {
            return <></>;
          }

          return (
            <TextEllipsis>
              <Text weight="normal" text={`+${tags.length - 1}`} />
            </TextEllipsis>
          );
        }, [tags.length])}
      </TextContent>
    </>
  );
}
